import {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {EntityInfo} from '../backend/interfaces/entity-info';
import {
  ExportErrorNotificationChannel,
  useExportDataWithErrorHandler,
} from './use-export-data-with-error-handler';
import {exportSeniorProfileGetReportData} from '../utils/data/export-data/senior-profile/base/export-senior-profile-get-report-data';
import {exportSeniorProfileProcessReportData} from '../utils/data/export-data/senior-profile/base/export-senior-profile-process-report-data';
import {prepareSeniorProfileDataPdf} from '../utils/data/export-data/senior-profile/export-senior-profile-pdf';

export function useSeniorProfilePdfExport({
  errorNotificationChannel = 'office',
}: {errorNotificationChannel?: ExportErrorNotificationChannel} = {}) {
  const {t} = useTranslation();
  const {previewPdfWithErrorHandling} = useExportDataWithErrorHandler(errorNotificationChannel);

  const handleExportPdf = useCallback(
    async ({seniorId, entity}: {seniorId?: string; entity?: EntityInfo}) => {
      await previewPdfWithErrorHandling(async () => {
        const entityId = entity?.id;

        if (!entityId || !seniorId) {
          console.error('Missing entityId or seniorId for exporting senior profile report');
          return null;
        }

        if (!entity) {
          console.error('No entity found in session for exporting senior profile report');
          return null;
        }

        const reportData = await exportSeniorProfileGetReportData({
          entityId,
          seniorId,
        });
        if (!reportData) {
          return null;
        }

        const preparedData = exportSeniorProfileProcessReportData(reportData);

        const pdf = await prepareSeniorProfileDataPdf({
          preparedData,
          entity,
          t,
        });

        return {
          file: pdf.file,
          fileName: pdf.fileName,
        };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [previewPdfWithErrorHandling, t]
  );

  return {
    handleExportPdf,
  };
}
